/**
 * Site Header
 * Top-most navigation bar containing search, company logo, and primary navigation.
 *
 * tabIndex order:
 * 		1. logo
 * 		2. search
 * 		3. nav items in order of appearance
 * 		10. sign in/account
 * 		11. favorites
 * 		12. cart
 *
 * 	This component takes up tabIndex numbers 1-20.
 */
import React from 'react';
import PropTypes from 'prop-types';
import styles from './styles.css';
import classNames from 'classnames';
import autobind from 'class-autobind';

// Components
import Icon from '../Icon';
import Link from '../Link';

// Utils
import { navigationWhitelist as navigationWhitelistConfig, routes as routesConfig } from 'config';
import { common as CommonLanguage, siteHeader as siteHeaderLanguage } from 'language';
// Lodash
import { filter, get, indexOf, extend, merge } from 'lodash';

// Logo SVG
const Logo = require('images/icons/optimized/logo.svg');
const LogoX = require('images/icons/optimized/logo_xsmall.svg');
class SiteHeader extends React.Component {
	constructor(props) {
		super(props);
		autobind(this);
	}

	removeMenuQueryParams() {
		const { changeRoute, location } = this.props;
		const query = merge({}, location.query);
		delete query.menu;
		delete query.subMenu;
		changeRoute(extend({}, location, { query }));
	}

	render() {
		const {
			cart,
			className,
			currentRoute,
			favoritesCount,
			isAuthenticated,
			location,
			routes,
			isDimmed,
			selectedMenu,
			onSetSubMenu,
			onSetMenu,
			breakpoints
		} = this.props;
		const primaryItems = [];
		const cartSize = get(cart, 'itemsInCart', 0);
		const companyName = breakpoints.ltSmall ? <LogoX className={ styles.logoIMG } /> : <Logo className={ styles.logoIMG } />;
		// Gather all routes with the `menuLevel: 1` property
		const topLevelRoutes = filter(routes, route => route.menuLevel === 1);

		if (topLevelRoutes) {
			// tabindex starts at 3 because search and logo links take up 1 and 2.
			let tabindex = 3;

			topLevelRoutes.forEach((route) => {
				if (route.name === 'discover' || route.name === 'try') {
					if (breakpoints.large) { return; }
				}

				const routeConfigItem = routesConfig[route.name];
				if (!routeConfigItem) { return; }

				if (
					routeConfigItem.breadcrumbBar
					&& route.pathname !== 'account'
					&& indexOf(navigationWhitelistConfig, route.name) > -1
				) {
					// Check to see if this route should open a menu
					const outgoing = {
						className: classNames(
							styles.primaryLink, {
								[styles['primaryLink--active']]: route.name === selectedMenu || (!route.opensMenu && (currentRoute && currentRoute.name === route.name))
							}
						),
						onClick: route.opensMenu ? () => {
							this.removeMenuQueryParams();
							onSetSubMenu('');
							onSetMenu(route.name);
						} : null,
						to: route.opensMenu ? null : route.path
					};

					primaryItems.push(
						<li
							className={ styles.primaryItem }
							key={ route.name }
						>
							<Link
								{ ...outgoing }
								tabIndex={ tabindex }
							>
								{ route.label }
							</Link>
						</li>
					);
					tabindex += 1;
				}
			});
		}

		return (
			<header
				className={ classNames(
					styles.siteHeader,
					className,
					isDimmed && styles.isDimmed
				) }
				role="banner"
			>
				<nav className={ styles.siteNavigation }>
					<Link
						className={ styles.logo }
						tabIndex="1"
						to="/"
					>
						{companyName}
					</Link>

					{ /* In-header navigation for larger breakpoints */ }
					<ul className={ styles.inHeaderNav }>
						{ primaryItems }
					</ul>

					{ /* SignIn (Unauthenticated Users) */ }
					{
						!isAuthenticated &&
						<Link
							className={ classNames(styles.yourAccount) }
							tabIndex="9"
							to={ { pathname: '/account/signin', state: { next: location.pathname } } }
						>
							{ siteHeaderLanguage.greeting.unknown }
							<Icon className={ classNames(styles.yourAccountIcon) } iconName="account" tabIndex="-1" title="View your account" />
						</Link>
					}

					{ /* Your Account (Authenticated Users) */ }
					{
						isAuthenticated &&
						<Link
							className={ classNames(styles.yourAccount, { [styles['account--deactive']]: selectedMenu === 'account' }, { [styles['account--deactive']]: selectedMenu !== 'account' }) }
							href="/account"
							onClick={ () => {
								this.removeMenuQueryParams();
								onSetSubMenu('');
								onSetMenu('account');
							} }
							tabIndex="10"
						>
							<Icon className={ styles['icon--account'] } iconName="account" tabIndex="-1" title="View your account" />
						</Link>
					}

					{ /* Search */ }
					<Link
						className={ styles.search }
						onClick={ () => {
							this.removeMenuQueryParams();
							onSetSubMenu('');
							onSetMenu('search');
						} }
						tabIndex="11"
					>
						<Icon className={ styles['icon--search'] } iconName="search" tabIndex="-1" title="Search typography.com" />
					</Link>

					{ /* Favorites */ }
					<Link
						className={ styles.favorites }
						tabIndex="12"
						to="/favorites"
					>
						<Icon className={ styles['icon--favorites'] } iconName="favorites" iconState={ favoritesCount > 0 ? 'active' : 'default' } tabIndex="-1" title="View your favorites" />
					</Link>

					{ /* Cart */ }
					<Link
						className={ styles.cart }
						tabIndex="13"
						to="/cart"
					>
						<Icon className={ styles['icon--cart-item'] } iconName={ cartSize ? 'cart-item' : 'cart' } iconState={ cartSize ? 'active' : 'default' } tabIndex="-1" title="Your cart" />
						{ cartSize ? <span className={ styles.cartCounter }>{ cartSize }</span> : null }
					</Link>
				</nav>
			</header>
		);
	}
}

SiteHeader.displayName = 'SiteHeader';

SiteHeader.propTypes = {
	cart: PropTypes.object,
	changeRoute: PropTypes.func.isRequired,
	className: PropTypes.string,
	currentRoute: PropTypes.object,
	favoritesCount: PropTypes.number,
	isAuthenticated: PropTypes.bool,
	isDimmed: PropTypes.bool,
	location: PropTypes.object.isRequired,
	onSetMenu: PropTypes.func.isRequired,
	onSetSubMenu: PropTypes.func.isRequired,
	routes: PropTypes.array,
	selectedMenu: PropTypes.string,
	breakpoints: PropTypes.object.isRequired
};

SiteHeader.defaultProps = {
	cart: null,
	className: null,
	currentRoute: null,
	favoritesCount: 0,
	isAuthenticated: null,
	isDimmed: null,
	routes: null,
	selectedMenu: null
};

export default SiteHeader;
